import { FC, useCallback } from "react"
import parse, { DOMNode, Element, domToReact } from "html-react-parser"
import { Alert } from "@mui/material"
import { renderMarkdown } from "./renderMarkdown"
import { renderEntities, MessageEntity } from "./entityRenderer"

interface TelegramTextContentProps {
  text: string
  parseMode?: string | null // 'HTML' | 'Markdown' | 'MarkdownV2' | null/undefined
  entities?: MessageEntity[]
}

/**
 * Renders Telegram message text according to the active parse_mode.
 *
 * HTML       → html-react-parser with custom replace for <tg-spoiler>/<tg-emoji>
 * Markdown   → renderMarkdown (custom Telegram spec renderer)
 * MarkdownV2 → renderMarkdown (custom Telegram spec renderer)
 * null/undef → renderEntities (MessageEntity[] offset/length annotation)
 *
 * Errors in HTML parsing are caught and rendered as an Alert instead of
 * crashing the component tree.
 */
export const TelegramTextContent: FC<TelegramTextContentProps> = ({
  text,
  parseMode,
  entities = [],
}) => {
  const spoilerReplace = useCallback((domNode: DOMNode) => {
    if (domNode instanceof Element) {
      // <tg-spoiler> is not standard HTML; map it to our styled span
      if (domNode.name === "tg-spoiler") {
        return (
          <span
            className="tg-spoiler"
            onClick={(e) =>
              (e.currentTarget as HTMLElement).classList.toggle(
                "tg-spoiler--revealed",
              )
            }
          >
            {domToReact(domNode.children as DOMNode[])}
          </span>
        )
      }
      // <span class="tg-spoiler"> from Telegram HTML mode — add click handler
      if (domNode.name === "span" && domNode.attribs?.class === "tg-spoiler") {
        return (
          <span
            className="tg-spoiler"
            onClick={(e) =>
              (e.currentTarget as HTMLElement).classList.toggle(
                "tg-spoiler--revealed",
              )
            }
          >
            {domToReact(domNode.children as DOMNode[])}
          </span>
        )
      }
      // <tg-emoji> — just render children (fallback: no custom emoji in preview)
      if (domNode.name === "tg-emoji") {
        return <>{domToReact(domNode.children as DOMNode[])}</>
      }
    }
  }, [])

  if (!text) return null

  try {
    if (parseMode === "HTML") {
      return (
        <div className="tg-bubble-text">
          {parse(text, { replace: spoilerReplace })}
        </div>
      )
    }

    if (parseMode === "Markdown") {
      return (
        <div className="tg-bubble-text">{renderMarkdown(text, "Markdown")}</div>
      )
    }

    if (parseMode === "MarkdownV2") {
      return (
        <div className="tg-bubble-text">
          {renderMarkdown(text, "MarkdownV2")}
        </div>
      )
    }

    // Quill / entities mode
    return (
      <div className="tg-bubble-text">{renderEntities(text, entities)}</div>
    )
  } catch (err) {
    return (
      <Alert severity="error">
        Ошибка рендеринга: {err instanceof Error ? err.message : String(err)}
      </Alert>
    )
  }
}
